import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TokenIcon from "@/components/TokenIcon";
import CountUp from "@/components/CountUp";
import { EVM_TOKENS, SOL_TOKENS } from "@/data/tokens";
import { ArrowRight, Clock, Flame, Activity, Crown, Zap } from "lucide-react";

/**
 * Modern 24h Boost feature panel.
 * Left  : large rotating featured pool card with capacity & CTA
 * Right : live leaderboard list (top 8 boost APRs) — clickable, auto-rotates
 */
export default function BoostCarousel() {
  const top = useMemo(() => {
    return [...EVM_TOKENS, ...SOL_TOKENS]
      .map(t => ({ ...t, chain: EVM_TOKENS.includes(t) ? "evm" : "solana" }))
      .sort((a, b) => b.boostApr - a.boostApr)
      .slice(0, 8);
  }, []);

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % top.length), 4200);
    return () => clearInterval(t);
  }, [paused, top.length]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const nextUtcMidnight = (() => {
    const d = new Date(now);
    const utc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, 0, 0, 0);
    return utc - now;
  })();
  const hh = String(Math.floor(nextUtcMidnight / 3600000)).padStart(2, "0");
  const mm = String(Math.floor((nextUtcMidnight % 3600000) / 60000)).padStart(2, "0");
  const ss = String(Math.floor((nextUtcMidnight % 60000) / 1000)).padStart(2, "0");

  const active = top[idx];
  const maxBoost = top.length ? top[0].boostApr : 1;

  return (
    <section
      className="relative overflow-hidden border-b"
      style={{ borderColor: "var(--border)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="boost-carousel"
    >
      {/* Ambient background glow */}
      <div className="absolute inset-0 -z-10 opacity-70 pointer-events-none" style={{
        background:
          "radial-gradient(40% 60% at 25% 30%, rgba(255,69,0,0.15), transparent 65%), radial-gradient(35% 55% at 80% 70%, rgba(255,20,147,0.10), transparent 65%)",
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-5 sm:gap-6 mb-8 sm:mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full border glow-pulse mb-4 sm:mb-5 relative overflow-hidden" style={{ borderColor: "rgba(255,180,80,0.4)", background: "rgba(255,69,0,0.08)" }} data-testid="boost-pulse-tag">
              <span className="inline-block w-1.5 h-1.5 rounded-full grad-boost-bg pulse-dot" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-mono grad-boost-text font-bold relative z-10">LIVE · 24H BOOST AUCTIONS</span>
              <Zap size={10} className="grad-boost-text flicker" />
              <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,180,80,0.18) 50%, transparent 100%)", backgroundSize: "200% 100%", animation: "borderSweep 4s linear infinite" }} />
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Today's <span className="grad-boost-text shimmer-text">hottest pools</span>.
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base" style={{ color: "var(--text-2)" }}>
              Validator priority auctions paying out right now. Capacity caps at 12% of TVL per asset.
            </p>
          </div>

          {/* Settlement countdown */}
          <div className="border px-4 sm:px-5 py-3 sm:py-3.5 inline-flex items-center gap-3 sm:gap-4 relative overflow-hidden lift" style={{ borderColor: "var(--border-strong)", background: "var(--surface-1)" }} data-testid="boost-countdown">
            <div className="absolute -top-12 -right-12 w-24 h-24 rounded-full grad-boost-bg opacity-15 drift pointer-events-none" />
            <Clock size={14} className="grad-boost-text float-y" />
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: "var(--text-3)" }}>Settles in</div>
              <div className="font-mono font-bold text-lg sm:text-xl tracking-tight leading-none mt-1 grad-boost-text tabular-nums">
                {hh}:{mm}:{ss}
              </div>
            </div>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-px" style={{ background: "var(--border)" }}>
          {/* Featured card */}
          <FeaturedCard key={active.symbol} token={active} settleIn={`${hh}:${mm}:${ss}`} />

          {/* Leaderboard */}
          <div className="lg:col-span-5" style={{ background: "var(--surface-1)" }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <Activity size={13} className="grad-boost-text" />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "var(--text-2)" }}>Live Leaderboard</span>
              </div>
              <span className="font-mono text-[10px]" style={{ color: "var(--text-3)" }}>top {top.length}</span>
            </div>
            <ul data-testid="boost-leaderboard">
              {top.map((t, i) => (
                <BoostRow
                  key={t.symbol}
                  rank={i + 1}
                  token={t}
                  pct={(t.boostApr / maxBoost) * 100}
                  active={i === idx}
                  onClick={() => setIdx(i)}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ token, settleIn }) {
  const cap = Math.max(8, Math.min(92, 100 - (token.boostApr / 4.5)));

  return (
    <div
      data-testid={`carousel-active-${token.symbol.toLowerCase()}`}
      className="lg:col-span-7 relative overflow-hidden card-swap"
      style={{ background: "var(--surface-1)" }}
    >
      <div className="absolute -top-px left-0 right-0 h-[2px] grad-boost-bg" />
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-25 grad-boost-bg drift pointer-events-none" />
      <div className="absolute -bottom-24 -left-12 w-64 h-64 rounded-full opacity-10 grad-boost-bg drift pointer-events-none" style={{ animationDelay: "6s" }} />

      <div className="relative p-6 sm:p-8 lg:p-10">
        {/* Top row */}
        <div className="flex items-start justify-between mb-6 sm:mb-7 gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="relative float-y flex-shrink-0">
              <div className="absolute inset-0 rounded-full grad-boost-bg blur-md opacity-60 scale-pulse" />
              <TokenIcon symbol={token.symbol} color={token.color} size="lg" className="relative" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Crown size={12} className="grad-boost-text" />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold grad-boost-text">leader</span>
              </div>
              <div className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none truncate">{token.symbol}</div>
              <div className="text-xs mt-1.5 truncate" style={{ color: "var(--text-3)" }}>{token.name} · {token.chain.toUpperCase()}</div>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1 grad-boost-bg text-[9px] sm:text-[10px] font-bold tracking-widest flex-shrink-0" style={{ color: "#fff" }}>
            <Flame size={11} className="flicker" /> 24H<span className="hidden sm:inline">&nbsp;BOOST</span>
          </div>
        </div>

        {/* Headline APR — count-up animated */}
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: "var(--text-3)" }}>Boost APR · realised last cycle</div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-mono font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter grad-boost-text leading-none number-tick tabular-nums">
              <CountUp key={token.symbol + "-apr"} end={token.boostApr} decimals={1} duration={900} />
            </div>
            <div className="font-display font-black text-2xl sm:text-3xl grad-boost-text">%</div>
            <div className="ml-auto text-right">
              <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>base apr</div>
              <div className="font-mono font-bold text-base sm:text-lg tabular-nums" style={{ color: "var(--text-2)" }}>
                <CountUp key={token.symbol + "-base"} end={token.apr} decimals={1} duration={900} />%
              </div>
            </div>
          </div>
        </div>

        {/* Capacity bar with shimmer overlay */}
        <div className="mb-7">
          <div className="flex items-center justify-between text-[11px] mb-1.5" style={{ color: "var(--text-2)" }}>
            <span className="uppercase tracking-widest text-[9px]">Pool capacity</span>
            <span className="font-mono tabular-nums"><span className="grad-boost-text font-bold">{cap.toFixed(0)}%</span> remaining</span>
          </div>
          <div className="h-1.5 w-full relative overflow-hidden" style={{ background: "var(--surface-3)" }}>
            <div className="absolute inset-y-0 left-0 grad-boost-bg overflow-hidden shimmer-bar" style={{ width: `${cap}%`, transition: "width 1.5s ease" }} />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-px mb-7" style={{ background: "var(--border)" }}>
          <Kpi label="Min entry"  value={`${token.minStake} ${token.symbol}`} />
          <Kpi label="Cycle"      value="24h" />
          <Kpi label="Settles"    value={settleIn} mono />
        </div>

        <Link
          to={`/markets/${token.chain}`}
          data-testid={`carousel-cta-${token.symbol.toLowerCase()}`}
          className="group inline-flex items-center justify-center w-full h-12 grad-boost-bg btn-glow-boost font-bold tracking-wider text-sm relative overflow-hidden"
          style={{ color: "#fff" }}
        >
          {/* Sweeping shimmer */}
          <span className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)", backgroundSize: "200% 100%", animation: "borderSweep 3.5s linear infinite" }} />
          <span className="relative">JOIN {token.symbol} BOOST POOL</span>
          <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform relative" />
        </Link>
      </div>
    </div>
  );
}

function Kpi({ label, value, mono }) {
  return (
    <div className="px-4 py-3" style={{ background: "var(--surface-2)" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{label}</div>
      <div className={`font-bold text-sm mt-1 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function BoostRow({ rank, token, pct, active, onClick }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={`boost-row-${token.symbol.toLowerCase()}`}
        className="relative w-full text-left px-3 sm:px-5 py-3 sm:py-3.5 flex items-center gap-3 sm:gap-4 border-b transition-all group"
        style={{
          borderColor: "var(--border)",
          background: active ? "var(--surface-2)" : "transparent",
        }}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-[3px] grad-boost-bg transition-opacity"
          style={{ opacity: active ? 1 : 0 }}
        />

        <div className="font-mono font-bold text-[11px] sm:text-xs w-5 sm:w-6 text-center flex-shrink-0" style={{ color: active ? "#FFB339" : "var(--text-3)" }}>
          {String(rank).padStart(2, "0")}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 min-w-[80px] sm:min-w-[100px] flex-shrink-0">
          <TokenIcon symbol={token.symbol} color={token.color} size="sm" />
          <div className="min-w-0">
            <div className="font-display font-bold text-sm tracking-tight leading-none truncate">{token.symbol}</div>
            <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--text-3)" }}>{token.chain}</div>
          </div>
        </div>

        <div className="flex-1 min-w-0 hidden sm:block">
          <div className="h-1 w-full relative overflow-hidden" style={{ background: "var(--surface-3)" }}>
            <div
              className="absolute inset-y-0 left-0 grad-boost-bg transition-all duration-700"
              style={{ width: `${pct}%`, opacity: active ? 1 : 0.55 }}
            />
          </div>
        </div>

        <div className="font-mono font-bold text-sm sm:text-base tracking-tight tabular-nums grad-boost-text min-w-[55px] sm:min-w-[70px] text-right ml-auto">
          {token.boostApr.toFixed(0)}<span className="text-xs">%</span>
        </div>
      </button>
    </li>
  );
}
